import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Wallet,
  History,
  User,
  ChevronDown,
  AlertCircle,
  Bell,
  X,
  Camera,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { clientsApi } from "../../api/clients.api";
import { ImageUploadModal } from "../modals/ImageUploadModal";
import { API_BASE_URL } from "../../api/client";
import { toast } from "../../hooks/use-toast";
import { useSession } from "../../providers/SessionProvider";
import { useTranslation } from "react-i18next";


const Navbar = ({ userInfo: propUserInfo, accounts: propAccounts = [], onSelectAccount }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['common', 'settings']);
  const activeTabFromPath = location.pathname.substring(1) || "home";
  const [activeTab, setActiveTab] = useState(activeTabFromPath);
  const [modalVisible, setModalVisible] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const currentLanguage = i18n.language;
  const [scrolled, setScrolled] = useState(false);

  // Use global state from SessionProvider
  const { userData, selectedAccount, setSelectedAccount } = useSession();

  // Accounts to use - props if provided, otherwise from context
  const accounts = useMemo(() => 
    propAccounts.length > 0 ? propAccounts : (userData.accounts || []),
    [propAccounts, userData.accounts]
  );

  // Use props if provided, otherwise derive from selected account
  const userInfo = useMemo(() => propUserInfo || (selectedAccount ? {
    nom: selectedAccount.nom || selectedAccount.client?.nom || '',
    prenom: selectedAccount.prenom || selectedAccount.client?.prenom || '',
    code: selectedAccount.code || '',
    verified: selectedAccount.kycvalidation === 'verified' || selectedAccount.client?.kycStatus === 'verified',
    type_compte: selectedAccount.type_compte || selectedAccount.type,
    image: selectedAccount.image || selectedAccount.client?.imageUrl ? (
      (selectedAccount.image || selectedAccount.client?.imageUrl).startsWith('/uploads')
        ? `${API_BASE_URL}${selectedAccount.image || selectedAccount.client?.imageUrl}`
        : (selectedAccount.image || selectedAccount.client?.imageUrl)
    ) : null,
  } : {
    nom: "",
    prenom: "",
    code: "",
    verified: false,
  }), [propUserInfo, selectedAccount]);

  const languages = [
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "ht", name: "Kreyòl", flag: "🇭🇹" },
    { code: "es", name: "Español", flag: "🇪🇸" },
  ];

  const getAvatarColor = (nom, prenom) => {
    const colors = [
      "#0047D1",
      "#1d4ed8",
      "#3b82f6",
      "#06b6d4",
      "#10b981",
      "#f59e0b",
      "#ef4444",
      "#8b5cf6",
      "#ec4899",
      "#14b8a6",
    ];
    const initials = `${nom || ""}${prenom || ""}`;
    const charCode = initials.charCodeAt(0) || 0;
    return colors[charCode % colors.length];
  };

  // Set initial selectedAccount from accounts if not set
  useEffect(() => {
    if (!selectedAccount && accounts.length > 0) {
      setSelectedAccount(accounts[0]);
    }
  }, [accounts, selectedAccount, setSelectedAccount]);

  // Détection du scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Mettre à jour l'onglet actif en fonction de l'URL
  useEffect(() => {
    const path = location.pathname;
    if (path === "/home" || path === "/") {
      setActiveTab("home");
    } else if (path === "/wallet") {
      setActiveTab("wallet");
    } else if (path === "/history") {
      setActiveTab("history");
    } else if (path === "/profile") {
      setActiveTab("profile");
    }
  }, [location]);

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    if (tabName === "home") {
      navigate("/home");
    } else if (tabName === "scan") {
      navigate("/scan");
    } else {
      navigate(`/${tabName}`);
    }
  };

  const handlePhotoUpdate = async (newImage) => {
    try {
      await clientsApi.updateMyProfile({ imageUrl: newImage });
      toast({
        title: "Siksè",
        description: "Foto pwofil ou mete ajou avèk siksè",
        type: "success",
      });
      // Force reload to update context/UI
      window.location.reload();
    } catch (error) {
      console.error("Failed to update photo", error);
      toast({
        title: "Erè",
        description: "Echèk priz an kont foto a",
        type: "error",
      });
    }
  };

  const navItemVariants = {
    hidden: { y: -20, opacity: 0 },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut",
      },
    }),
  };

  const navItems = [
    { id: "home", label: t('common:navbar.home'), icon: Home },
    { id: "wallet", label: t('common:navbar.wallet'), icon: Wallet },
    { id: "history", label: t('common:navbar.history'), icon: History },
    { id: "profile", label: t('common:navbar.profile'), icon: User },
  ];

  return (
    <>
      {/* Desktop Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`hidden md:block fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
          ? "bg-white/95 backdrop-blur-xl"
          : "bg-white/60 backdrop-blur-lg"
          } border-b ${scrolled ? "border-gray-200" : "border-gray-100"}`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-16 lg:px-32">
          <div className="flex items-center justify-between h-20 gap-6">
            {/* User Header */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-3"
            >
              <div
                className="relative w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold border-2 cursor-pointer group"
                style={{
                  backgroundColor: userInfo.image ? "transparent" : getAvatarColor(
                    userInfo.nom,
                    userInfo.prenom
                  ),
                  borderColor: userInfo.image ? "#2563EB" : getAvatarColor(userInfo.nom, userInfo.prenom),
                }}
                onClick={() => setImageModalOpen(true)}
              >
                {userInfo.image ? (
                  <img
                    src={userInfo.image.startsWith('/uploads') ? `${API_BASE_URL}${userInfo.image}` : userInfo.image}
                    alt="Profile"
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  <>
                    {userInfo.nom?.charAt(0)}
                    {userInfo.prenom?.charAt(0)}
                  </>
                )}

                {/* Camera Overlay */}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                  <Camera size={16} className="text-white" />
                </div>

                <motion.div
                  className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </div>
              <div className="leading-tight">
                <p className="text-sm font-bold text-gray-900">
                  {t('common:greeting')}, {userInfo.nom} {userInfo.prenom}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setModalVisible(true)}
                    className="flex items-center gap-1 text-xs text-gray-500 font-medium"
                  >
                    #{userInfo.code}
                    <motion.div
                      animate={{ rotate: modalVisible ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </motion.div>
                  </button>
                  {!userInfo.verified && (
                    <motion.button
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      onClick={() => navigate('/profile')}
                      className="text-xs text-red-500 font-semibold flex items-center gap-1 bg-red-50 px-2 py-0.5 rounded-full hover:bg-red-100 transition-colors cursor-pointer"
                    >
                      <AlertCircle className="w-3.5 h-3.5" />
                      {t('common:navbar.unverified')}
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Navigation Items */}
            <div className="hidden md:flex md:items-center md:gap-2 bg-gray-50/50 rounded-full p-1.5">
              <AnimatePresence>
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    custom={index}
                    initial="hidden"
                    animate="visible"
                    variants={navItemVariants}
                    className="relative"
                  >
                    <button
                      onClick={() => handleTabClick(item.id)}
                      className={`group relative flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-full transition-all duration-300 ${activeTab === item.id ? "text-white" : "text-gray-600"
                        }`}
                    >
                      {activeTab === item.id && (
                        <motion.span
                          layoutId="activeNavPill"
                          className="absolute inset-0 rounded-full bg-blue-600"
                          transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 30,
                          }}
                        />
                      )}
                      <item.icon className="relative z-10 w-4 h-4" />
                      <span className="relative z-10">{item.label}</span>
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Section - Actions */}
            <div className="hidden md:flex items-center gap-3">
              {/* Notifications */}
              <button className="relative p-2.5 rounded-full bg-gray-50">
                <Bell className="w-5 h-5 text-gray-600" />
                <motion.span
                  className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </button>

              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full border-2 border-gray-200 bg-white"
                >
                  <span className="text-base">
                    {languages.find((l) => l.code === currentLanguage)?.flag}
                  </span>
                  <span className="text-xs font-semibold text-gray-700">
                    {languages.find((l) => l.code === currentLanguage)?.name}
                  </span>
                  <motion.div
                    animate={{ rotate: languageMenuOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {languageMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 top-full mt-3 bg-white rounded-xl border border-gray-200 w-44 overflow-hidden z-50"
                    >
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            console.log("Changing language to:", lang.code);
                            i18n.changeLanguage(lang.code);
                            setLanguageMenuOpen(false);
                            toast({
                              title: t('common:success'),
                              description: t('common:languageChanged'),
                              type: "success",
                            });
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${currentLanguage === lang.code
                            ? "bg-blue-50 border-l-4 border-blue-600"
                            : ""
                            }`}
                        >
                          <span className="text-lg">{lang.flag}</span>
                          <span
                            className={`text-xs font-semibold ${currentLanguage === lang.code
                              ? "text-blue-700"
                              : "text-gray-700"
                              }`}
                          >
                            {lang.name}
                          </span>
                          {currentLanguage === lang.code && (
                            <motion.span
                              layoutId="selectedLang"
                              className="ml-auto w-2 h-2 bg-blue-600 rounded-full"
                            />
                          )}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Navigation */}
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-lg border-t border-gray-100 shadow-lg"
      >
        <div className="grid grid-cols-4 py-2">
          {navItems.map((item, index) => (
            <motion.button
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.3 }}
              onClick={() => handleTabClick(item.id)}
              className={`flex flex-col items-center py-2 px-1 text-xs font-medium ${activeTab === item.id ? "text-blue-600" : "text-gray-500"
                }`}
            >
              <div
                className={`relative p-2 rounded-full ${activeTab === item.id ? "bg-blue-50" : ""
                  }`}
              >
                <item.icon
                  className={`w-5 h-5 ${activeTab === item.id ? "text-blue-600" : "text-gray-500"
                    }`}
                />

                {activeTab === item.id && (
                  <motion.span
                    className="absolute bottom-0 left-1/2 w-1 h-1 bg-blue-600 rounded-full -translate-x-1/2"
                    layoutId="mobileActiveDot"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </div>
              <span className="mt-1">{item.label}</span>
            </motion.button>
          ))}
        </div>
      </motion.div>


      {/* Modal Sélection Compte */}
      <AnimatePresence>
        {modalVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={() => setModalVisible(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[70vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                  {t('common:navbar.selectAccount')}
                </h3>
                <button
                  onClick={() => setModalVisible(false)}
                  className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => setModalVisible(false)}
                  className="w-full p-4 rounded-lg text-left transition-colors bg-blue-900 text-white"
                >
                  <span className="font-medium">{t('common:navbar.allAccounts')}</span>
                </button>

                {accounts.length > 0 ? accounts.map((compte) => (
                  <button
                    key={compte.code}
                    onClick={() => {
                      setSelectedAccount(compte);
                      if (onSelectAccount) onSelectAccount(compte);
                      setModalVisible(false);
                    }}
                    className={`w-full p-4 rounded-lg text-left transition-colors ${selectedAccount?.code === compte.code
                      ? 'bg-blue-50 border-2 border-blue-600'
                      : 'bg-gray-50 hover:bg-gray-100'
                      } text-gray-900`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-medium">{compte.nom || compte.client?.nom} {compte.prenom || compte.client?.prenom}</span>
                        <p className="text-xs text-gray-500">#{compte.code}</p>
                      </div>
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                        {compte.type_compte || compte.type || 'BS'} - {compte.devise || 'HTG'}
                      </span>
                    </div>
                  </button>
                )) : (
                  <p className="text-center text-gray-500 py-4">{t('common:navbar.noAccount')}</p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ImageUploadModal
        isOpen={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        onUpload={handlePhotoUpdate}
      />
    </>
  );
};

export default Navbar;
